import { IconLogin2, IconPlus } from '@tabler/icons-react';
import { type FormEvent, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Banner, Sheet, Spinner, useToast } from '@/components/common';
import { useResource } from '@/hooks';
import { errorMessage } from '@/lib/api-client';
import type { ClinicSummary } from '@/types/api';
import { useAuth } from '../auth';
import { formatDate } from '../chart/components';
import { platformApi } from './api';

function NewClinicForm({ onCreated }: { onCreated: (c: ClinicSummary) => void }) {
  const { t } = useTranslation();
  const toast = useToast();
  const [v, setV] = useState({ clinic: '', name: '', email: '', password: '' });
  const [busy, setBusy] = useState(false);
  const field = (key: keyof typeof v, type = 'text', extra: object = {}) => (
    <div className="field">
      <label htmlFor={`nc-${key}`}>{t(`settings.clinics.${key}`)}</label>
      <input
        id={`nc-${key}`}
        className="input"
        type={type}
        required
        value={v[key]}
        onChange={(e) => setV((x) => ({ ...x, [key]: e.target.value }))}
        {...extra}
      />
    </div>
  );

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { clinic } = await platformApi.createClinic({
        name: v.clinic.trim(),
        admin: { name: v.name.trim(), email: v.email.trim(), password: v.password },
      });
      onCreated(clinic);
    } catch (err) {
      toast.show(errorMessage(err, { CONFLICT: t('settings.users.exists') }), 'error');
    } finally {
      setBusy(false);
    }
  };

  return (
    <form className="stack" onSubmit={submit}>
      {field('clinic', 'text', { 'data-autofocus': true })}
      <p className="small muted">{t('settings.clinics.adminHint')}</p>
      {field('name')}
      {field('email', 'email')}
      {field('password', 'password', { minLength: 8, autoComplete: 'new-password' })}
      <button type="submit" className="btn btn--dark btn--block" disabled={busy}>
        {t('settings.clinics.create')}
      </button>
    </form>
  );
}

/** The clinics (tenants) of Dentlog: add, (de)activate, enter one to manage its settings and users. */
export function ClinicsPanel() {
  const { t } = useTranslation();
  const toast = useToast();
  const { enterClinic } = useAuth();
  const clinics = useResource(() => platformApi.clinics(), 'clinics');
  const [adding, setAdding] = useState(false);
  const [entering, setEntering] = useState<string | null>(null);

  const toggle = async (c: ClinicSummary) => {
    try {
      const updated = await platformApi.updateClinic(c.id, { active: !c.active });
      clinics.setData((list) => list?.map((x) => (x.id === c.id ? updated : x)));
    } catch (err) {
      toast.show(errorMessage(err), 'error');
    }
  };

  const enter = async (c: ClinicSummary) => {
    setEntering(c.id);
    try {
      await enterClinic(c.id);
    } catch (err) {
      toast.show(errorMessage(err), 'error');
      setEntering(null);
    }
  };

  if (clinics.error) return <Banner tone="error">{errorMessage(clinics.error)}</Banner>;
  if (!clinics.data) return <Spinner />;

  return (
    <section className="card">
      <div className="card__head">
        <h2>{t('settings.clinics.title')}</h2>
        <button type="button" className="btn btn--sm btn--dark" onClick={() => setAdding(true)}>
          <IconPlus size={18} /> {t('common.new')}
        </button>
      </div>
      <ul className="list">
        {clinics.data.map((c) => (
          <li key={c.id} className="user-row">
            <div className="grow">
              <strong>{c.name}</strong>
              {!c.active && <span className="badge">{t('settings.users.inactive')}</span>}
              <div className="small muted">
                {t('settings.clinics.users', { count: c.userCount })} · {formatDate(c.createdAt)}
              </div>
            </div>
            <label className="check">
              <input type="checkbox" checked={c.active} onChange={() => toggle(c)} />
              {t('settings.users.active')}
            </label>
            <button
              type="button"
              className="btn btn--sm"
              disabled={entering !== null}
              onClick={() => enter(c)}
              title={t('platform.enterTitle', { name: c.name })}
            >
              <IconLogin2 size={18} /> {t('platform.enter')}
            </button>
          </li>
        ))}
      </ul>
      {adding && (
        <Sheet title={t('settings.clinics.newTitle')} onClose={() => setAdding(false)}>
          <NewClinicForm
            onCreated={(c) => {
              clinics.setData((list) => [...(list ?? []), c]);
              setAdding(false);
            }}
          />
        </Sheet>
      )}
    </section>
  );
}
